import { zodResolver } from "@hookform/resolvers/zod";

import { useForm } from "react-hook-form";
import {
  APPROVE_AP_INVOICE_AMOUNT_LIMIT,
  APPROVE_CONTRACT_AMOUNT_LIMIT,
  APPROVE_WORK_ORDER_AMOUNT_LIMIT,
  APPROVE_PENALTIES_AMOUNT_LIMIT,
  APPROVED_BUDGET,
  DESCRIPTION,
  IS_ACTIVE,
  IS_APPROVE_PENALTIES,
  IS_APPROVE_AP_INVOICE,
  IS_APPROVE_CONTRACT,
  POSITION_NAME,
  IS_APPROVE_WORK_ORDER,
  APPROVAL,
  APPROVE_AP_INVOICE_AMOUNT_YEARLY_LIMIT,
} from "./consts";
import { positionFormValues, positionSchema } from "./validations";
import {
  getRequiredFields,
  removeEmptyFields,
  setEmptyFieldsToNull,
} from "@/lib/utils";
import { useAddPositionMutation, useUpdatePositionMutation } from "@/services";
import { toast } from "@/components/Toast";
import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";

const useAddEditPosition = ({
  selectedPosition,
  onCancel,
}: {
  selectedPosition: any;
  onCancel: () => void;
}) => {
  const queryClient = useQueryClient();
  const form = useForm<positionFormValues>({
    defaultValues: {
      [POSITION_NAME]: "",
      [APPROVED_BUDGET]: false,
      [IS_ACTIVE]: true,
      [IS_APPROVE_CONTRACT]: false,
      [APPROVE_CONTRACT_AMOUNT_LIMIT]: "",
      [IS_APPROVE_AP_INVOICE]: false,
      [APPROVE_AP_INVOICE_AMOUNT_LIMIT]: "",
      [APPROVE_AP_INVOICE_AMOUNT_YEARLY_LIMIT]: "",
      [IS_APPROVE_WORK_ORDER]: false,
      [APPROVE_WORK_ORDER_AMOUNT_LIMIT]: "",
      [IS_APPROVE_PENALTIES]: false,
      [APPROVE_PENALTIES_AMOUNT_LIMIT]: "",
      [DESCRIPTION]: "",
      [APPROVAL]: "",
    },
    resolver: zodResolver(positionSchema),
  });

  const requiredFields = getRequiredFields(positionSchema);

  const communityDetail = useSelector(
    (state: RootState) => state.currentData.communityData
  );

  useEffect(() => {
    if (selectedPosition) {
      form.reset({
        [POSITION_NAME]: selectedPosition?.[POSITION_NAME] || "",
        [APPROVED_BUDGET]: selectedPosition?.[APPROVED_BUDGET] || false,
        [IS_ACTIVE]: selectedPosition?.[IS_ACTIVE] || true,
        [IS_APPROVE_CONTRACT]: selectedPosition?.[IS_APPROVE_CONTRACT] || false,
        [APPROVE_CONTRACT_AMOUNT_LIMIT]:
          selectedPosition?.[APPROVE_CONTRACT_AMOUNT_LIMIT]?.toString() || "",
        [IS_APPROVE_AP_INVOICE]:
          selectedPosition?.[IS_APPROVE_AP_INVOICE] || false,
        [APPROVE_AP_INVOICE_AMOUNT_LIMIT]:
          selectedPosition?.[APPROVE_AP_INVOICE_AMOUNT_LIMIT]?.toString() || "",
        [APPROVE_AP_INVOICE_AMOUNT_YEARLY_LIMIT]:
          selectedPosition?.[
            APPROVE_AP_INVOICE_AMOUNT_YEARLY_LIMIT
          ]?.toString() || "",
        [IS_APPROVE_WORK_ORDER]:
          selectedPosition?.[IS_APPROVE_WORK_ORDER] || false,
        [APPROVE_WORK_ORDER_AMOUNT_LIMIT]:
          selectedPosition?.[APPROVE_WORK_ORDER_AMOUNT_LIMIT]?.toString() || "",
        [IS_APPROVE_PENALTIES]:
          selectedPosition?.[IS_APPROVE_PENALTIES] || false,
        [APPROVE_PENALTIES_AMOUNT_LIMIT]:
          selectedPosition?.[APPROVE_PENALTIES_AMOUNT_LIMIT]?.toString() || "",
        [DESCRIPTION]: selectedPosition?.[DESCRIPTION] || "",
      });
    }
  }, [selectedPosition, form]);

  // add position
  const { mutate: addPosition, isPending: isAddPositionPending } =
    useAddPositionMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        toast.success("Position Added Successfully");
        queryClient.invalidateQueries({
          queryKey: ["bom-position", communityDetail?.communityId],
        });
        onCancel();
      },
    });

  // update position
  const { mutate: updatePosition, isPending: isUpdatePositionPending } =
    useUpdatePositionMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        toast.success("Position Updated Successfully");
        queryClient.invalidateQueries({
          queryKey: ["bom-position", communityDetail?.communityId],
        });
        onCancel();
      },
    });

  const onSubmit = (data: positionFormValues) => {
    let _data = removeEmptyFields(data);
    _data = setEmptyFieldsToNull(_data);

    if (_data[IS_APPROVE_AP_INVOICE]) {
      if (!_data[APPROVE_AP_INVOICE_AMOUNT_YEARLY_LIMIT]) {
        form.setError(APPROVE_AP_INVOICE_AMOUNT_YEARLY_LIMIT, {
          message: "This field is required",
        });
        return;
      }
    }

    if (selectedPosition) {
      updatePosition({
        data: { ...data, communityId: communityDetail?.communityId as string },
        id: selectedPosition.bomPositionId,
      });
    } else {
      addPosition({
        data: { ...data, communityId: communityDetail?.communityId as string },
      });
    }
  };

  return {
    form,
    requiredFields,
    onSubmit,
    isAddPositionPending,
    isUpdatePositionPending,
  };
};

export default useAddEditPosition;
